import { useEffect, useRef, useState } from "react";
import { base44 } from "@/api/base44Client";
import { toast } from "sonner";

// Sound generator using Web Audio API
function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const frequencies = [523, 659, 784]; // C, E, G chord
    frequencies.forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0, ctx.currentTime + i * 0.12);
      gain.gain.linearRampToValueAtTime(0.25, ctx.currentTime + i * 0.12 + 0.05);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.4);
      osc.start(ctx.currentTime + i * 0.12);
      osc.stop(ctx.currentTime + i * 0.12 + 0.5);
    });
  } catch (_) {}
}

// Request browser notification permission
async function requestNotifPermission() {
  if ("Notification" in window && Notification.permission === "default") {
    await Notification.requestPermission();
  }
}

function showBrowserNotif(order) {
  if ("Notification" in window && Notification.permission === "granted") {
    new Notification("🧺 طلب جديد!", {
      body: `${order.customer_name} — ${order.customer_phone}\n${order.customer_address || ""}`,
      icon: "https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png",
      badge: "https://media.base44.com/images/public/user_69729b1c2410c5c031f1d29a/291b90bb1_.png",
      tag: `order-${order.id}`,
      requireInteraction: true,
    });
  }
}

export default function OrderNotifier() {
  const knownIdsRef = useRef(null); // null = first load
  const [enabled, setEnabled] = useState(() => {
    return localStorage.getItem("order_notif_enabled") !== "false";
  });

  // Request permission on mount
  useEffect(() => {
    requestNotifPermission();
  }, []);

  // Poll every 15 seconds for new pending orders
  useEffect(() => {
    if (!enabled) return;

    const check = async () => {
      try {
        const orders = await base44.entities.LaundryOrder.filter({ status: "pending" }, "-created_date", 50);
        const ids = new Set(orders.map(o => o.id));

        if (knownIdsRef.current === null) {
          // First load — just store current IDs, don't notify
          knownIdsRef.current = ids;
          return;
        }

        const newOrders = orders.filter(o => !knownIdsRef.current.has(o.id));

        if (newOrders.length > 0) {
          playNotificationSound();

          newOrders.forEach(order => {
            showBrowserNotif(order);

            toast.custom(
              (id) => (
                <div
                  className="bg-white dark:bg-zinc-900 border-2 border-cyan-500 rounded-2xl shadow-2xl px-4 py-4 flex items-start gap-3 max-w-sm w-full"
                  dir="rtl"
                >
                  <div className="w-10 h-10 bg-cyan-100 dark:bg-cyan-900/40 rounded-xl flex items-center justify-center text-xl flex-shrink-0">
                    📦
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-black text-sm text-cyan-700 dark:text-cyan-400">طلب جديد وصل!</p>
                    <p className="font-bold text-foreground text-sm truncate">{order.customer_name}</p>
                    <p className="text-xs text-muted-foreground">{order.customer_phone}</p>
                    {order.customer_address && (
                      <p className="text-xs text-muted-foreground truncate">📍 {order.customer_address}</p>
                    )}
                    <a
                      href="/admin/laundry/orders"
                      onClick={() => toast.dismiss(id)}
                      className="inline-block mt-2 px-3 py-1 bg-cyan-600 text-white text-xs font-bold rounded-lg hover:bg-cyan-700 transition-colors"
                    >
                      عرض الطلب ←
                    </a>
                  </div>
                  <button
                    onClick={() => toast.dismiss(id)}
                    className="text-muted-foreground hover:text-foreground text-lg leading-none flex-shrink-0"
                  >
                    ✕
                  </button>
                </div>
              ),
              { duration: 12000, id: `new-order-${order.id}` }
            );
          });

          // Update known IDs
          newOrders.forEach(o => knownIdsRef.current.add(o.id));
        }
      } catch (_) {}
    };

    check(); // immediate first check
    const interval = setInterval(check, 15000);
    return () => clearInterval(interval);
  }, [enabled]);

  const toggle = () => {
    const next = !enabled;
    setEnabled(next);
    localStorage.setItem("order_notif_enabled", next ? "true" : "false");
    if (next) knownIdsRef.current = null; // reset so next check re-seeds
    toast.success(next ? "🔔 التنبيهات مفعّلة" : "🔕 التنبيهات موقفة");
  };

  return (
    <button
      onClick={toggle}
      title={enabled ? "إيقاف تنبيهات الطلبات" : "تفعيل تنبيهات الطلبات"}
      className={`relative flex items-center justify-center w-9 h-9 rounded-xl transition-all ${
        enabled
          ? "bg-cyan-100 dark:bg-cyan-900/40 text-cyan-600 dark:text-cyan-400 hover:bg-cyan-200 dark:hover:bg-cyan-900/60"
          : "bg-secondary text-muted-foreground hover:bg-secondary/80"
      }`}
    >
      <span className="text-lg">{enabled ? "🔔" : "🔕"}</span>
      {enabled && (
        <span className="absolute top-1 right-1 w-2 h-2 bg-cyan-500 rounded-full animate-pulse" />
      )}
    </button>
  );
}